/* eslint-disable array-callback-return */
import { Button, Divider, FormControl, Grid2, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';

// components & hooks
import MainCard from 'components/MainCard';
import BackToggle from 'components/third-party/dropzone/BackToggle';
import RequiredIndicator from 'components/@extended/RequiredIndicator';
import Snackbar from 'utils/Snackbar';

import { FormattedMessage, useIntl } from 'react-intl';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';

// services
import { getAllDivision } from 'services/division.service';
import { getAllSubjects } from 'services/subject.service';
import { getFilterListUser } from 'services/user.service';
import { addTeacherWorkload, editTeacherWorkload } from 'services/workload.service';

// utils
import { getLocaleName } from 'utils/helper';

function AddTeacherWorkload() {
  const location = useLocation();
  const rowData: any = location.state?.rowData ?? {};

  const navigate = useNavigate();
  const intl = useIntl();
  const { i18n } = useConfig();
  const { user }: any = useAuth();

  const organizationId = user?.organizationId?._id ?? null;

  const isEditMode = Boolean(rowData?._id); // Detect edit mode

  const [placeValues, setPlaceValues] = useState<any>();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const [divisionOptions, setDivisionOptions] = useState<any[]>([]);
  const [subjectOptions, setSubjectOptions] = useState<any[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<any[]>([]);
  const [academicYearOptions, setAcademicYearOptions] = useState<any[]>([]);

  /* ---------------- LOCALE ---------------- */
  useEffect(() => {
    getLocaleName(i18n)
      .then((res) => setPlaceValues(res))
      .catch(() => {});
  }, [i18n]);

  /* ---------------- FETCH DIVISIONS & ACADEMIC YEARS ---------------- */
  useEffect(() => {
    if (!organizationId) return;

    const fetchDivisions = async () => {
      try {
        const res = await getAllDivision({ organizationId });
        if (res.status === 200 && res.data?.data?.divisions) {
          const divisions = res.data.data.divisions;

          setDivisionOptions(
            divisions.map((d: any) => ({
              value: d._id,
              label: d.divisionName
            }))
          );

          const years = Array.from(new Set(divisions.map((d: any) => d.academicYear).filter(Boolean)))
            .sort()
            .reverse() // Optional: newest year first
            .map((y: any) => ({ value: y, label: y }));

          setAcademicYearOptions(years);
        }
      } catch {
        Snackbar('Failed to fetch divisions', 'error');
      }
    };

    fetchDivisions();
  }, [organizationId]);

  /* ---------------- FETCH SUBJECTS ---------------- */
  useEffect(() => {
    if (!organizationId) return;

    getAllSubjects({ organizationId })
      .then((res) => {
        if (res.status === 200 && res.data?.data?.data) {
          setSubjectOptions(
            res.data.data.data.map((s: any) => ({
              value: s._id,
              label: s.subjectName
            }))
          );
        }
      })
      .catch(() => Snackbar('Failed to fetch subjects', 'error'));
  }, [organizationId]);

  /* ---------------- FETCH TEACHERS (FACULTY) ---------------- */
  useEffect(() => {
    if (!organizationId) return;

    getFilterListUser({ orgId: organizationId, type: 'faculty' })
      .then((result) => {
        if (result.status === 200 && result?.data?.data) {
          const facultyList = result.data.data;

          const formattedFaculty = facultyList.map((fac: any) => ({
            value: fac._id,
            label: `${fac.firstName || 'Unknown'} ${fac.lastName || ''}`.trim()
          }));

          setTeacherOptions(
            formattedFaculty.length > 0
              ? formattedFaculty
              : [
                  { value: 'static-1', label: 'Static Teacher 1' },
                  { value: 'static-2', label: 'Static Teacher 2' },
                  { value: 'static-3', label: 'Static Teacher 3' }
                ]
          );
        } else {
          setTeacherOptions([
            { value: 'static-1', label: 'Static Teacher 1' },
            { value: 'static-2', label: 'Static Teacher 2' },
            { value: 'static-3', label: 'Static Teacher 3' }
          ]);
        }
      })
      .catch((err) => {
        console.error('Error fetching faculty:', err);
        Snackbar('Failed to fetch teachers', 'error');
        setTeacherOptions([
          { value: 'static-1', label: 'Static Teacher 1' },
          { value: 'static-2', label: 'Static Teacher 2' },
          { value: 'static-3', label: 'Static Teacher 3' }
        ]);
      });
  }, [organizationId]);

  /* ---------------- FORM ---------------- */
  const formik = useFormik({
    initialValues: {
      divisionId: rowData.divisionId?._id || rowData.divisionId || '',
      subjectId: rowData.subjectId?._id || rowData.subjectId || '',
      teacherId: rowData.teacherId?._id || rowData.teacherId || '',
      maxLectures: rowData.maxLectures || '',
      academicYear: rowData.academicYear || ''
    },

    validationSchema: yup.object({
      divisionId: yup.string().required('Division is required'),
      subjectId: yup.string().required('Subject is required'),
      teacherId: yup.string().required('Teacher is required'),
      maxLectures: yup.number().min(1, 'Minimum 1 lecture').required('Max lectures is required'),
      academicYear: yup.string().required('Academic year is required')
    }),

    enableReinitialize: true, // Important: allows form to reset when rowData changes (edit mode)

    onSubmit: async (values) => {
      setIsButtonDisabled(true);
      try {
        const payload = {
          divisionId: values.divisionId,
          subjectId: values.subjectId,
          teacherId: values.teacherId,
          maxLectures: Number(values.maxLectures),
          academicYear: values.academicYear,
          organizationId
        };

        let res;
        if (isEditMode) {
          // Edit existing workload
          res = await editTeacherWorkload({
            teacherWorkloadId: rowData._id, // Pass the ID
            ...payload
          });
        } else {
          // Add new workload
          res = await addTeacherWorkload(payload);
        }

        if (res.status === 200 || res.status === 201) {
          const msg = isEditMode
            ? res.data.message || 'Teacher workload updated successfully'
            : res.data.message || 'Teacher workload added successfully';

          Snackbar(msg, 'success');
          navigate('/teacherworkload', { replace: true });
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.message || (isEditMode ? 'Failed to update teacher workload' : 'Failed to add teacher workload');
        Snackbar(message, 'error');
      } finally {
        setIsButtonDisabled(false);
      }
    }
  });

  return (
    <Grid2 container justifyContent="center">
      <Grid2 size={{ xs: 12 }}>
        <MainCard
          title={
            <Box display="flex" alignItems="center">
              <BackToggle show />
              <Box ml={2}>
                <Typography variant="h5">
                  <FormattedMessage id={isEditMode ? 'Edit Teacher Workload' : 'Add Teacher Workload'} />
                </Typography>
                <Typography variant="body2">
                  <FormattedMessage id={isEditMode ? 'Update teacher workload details' : 'Create teacher workload details'} />
                </Typography>
              </Box>
            </Box>
          }
          content={false}
        >
          <form onSubmit={formik.handleSubmit}>
            <Box p={3}>
              <Grid2 container spacing={3}>
                {/* Division */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <InputLabel>
                    Division Name <RequiredIndicator />
                  </InputLabel>
                  <FormControl fullWidth error={Boolean(formik.touched.divisionId && formik.errors.divisionId)}>
                    <Select
                      name="divisionId"
                      value={formik.values.divisionId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select division
                      </MenuItem>
                      {divisionOptions.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.divisionId && formik.errors.divisionId && (
                      <Typography variant="caption" color="error">
                        {formik.errors.divisionId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid2>

                {/* Subject */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <InputLabel>
                    Subject <RequiredIndicator />
                  </InputLabel>
                  <FormControl fullWidth error={Boolean(formik.touched.subjectId && formik.errors.subjectId)}>
                    <Select
                      name="subjectId"
                      value={formik.values.subjectId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select subject
                      </MenuItem>
                      {subjectOptions.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.subjectId && formik.errors.subjectId && (
                      <Typography variant="caption" color="error">
                        {formik.errors.subjectId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid2>

                {/* Teacher */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <InputLabel>
                    Teacher <RequiredIndicator />
                  </InputLabel>
                  <FormControl fullWidth error={Boolean(formik.touched.teacherId && formik.errors.teacherId)}>
                    <Select
                      name="teacherId"
                      value={formik.values.teacherId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select teacher
                      </MenuItem>
                      {teacherOptions.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.teacherId && formik.errors.teacherId && (
                      <Typography variant="caption" color="error">
                        {formik.errors.teacherId}
                      </Typography>
                    )}
                  </FormControl>
                </Grid2>

                {/* Max Lectures */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <InputLabel>
                    Max Lectures Per Week <RequiredIndicator />
                  </InputLabel>
                  <TextField
                    fullWidth
                    type="number"
                    name="maxLectures"
                    value={formik.values.maxLectures}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.touched.maxLectures && formik.errors.maxLectures)}
                    helperText={formik.touched.maxLectures && formik.errors.maxLectures}
                  />
                </Grid2>

                {/* Academic Year */}
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <InputLabel>
                    Academic Year <RequiredIndicator />
                  </InputLabel>
                  <FormControl fullWidth error={Boolean(formik.touched.academicYear && formik.errors.academicYear)}>
                    <Select
                      name="academicYear"
                      value={formik.values.academicYear}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select academic year
                      </MenuItem>
                      {academicYearOptions.map((y) => (
                        <MenuItem key={y.value} value={y.value}>
                          {y.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.academicYear && formik.errors.academicYear && (
                      <Typography variant="caption" color="error">
                        {formik.errors.academicYear}
                      </Typography>
                    )}
                  </FormControl>
                </Grid2>
              </Grid2>
            </Box>

            <Divider />
            <Stack direction="row" justifyContent="flex-end" spacing={2} p={2}>
              <Button type="submit" variant="contained" disabled={isButtonDisabled || !formik.isValid || formik.isSubmitting}>
                <FormattedMessage id={isEditMode ? 'Update' : 'Add'} />
              </Button>
              <Button variant="outlined" color="error" onClick={() => navigate(-1)}>
                <FormattedMessage id="Cancel" />
              </Button>
            </Stack>
          </form>
        </MainCard>
      </Grid2>
    </Grid2>
  );
}

export default AddTeacherWorkload;
